import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Play, Pause, RotateCcw, Timer, AlarmClock, Check, Clock3 } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import {
    readActiveFocusSessionSummary,
    getLiveFocusSessionSnapshot,
    formatSeconds as formatFocusSeconds,
    ACTIVE_FOCUS_SESSION_EVENT,
    type ActiveFocusSessionSummary,
} from "../../utils/timeTracking";

interface TimerPanelProps {
    isOpen: boolean;
    onClose?: () => void;
    isHomePage?: boolean;
}

type TimerMode = "timer" | "stopwatch";

const PRESETS = [
    { label: "5 dk", minutes: 5, seconds: 0 },
    { label: "10 dk", minutes: 10, seconds: 0 },
    { label: "15 dk", minutes: 15, seconds: 0 },
    { label: "25 dk", minutes: 25, seconds: 0 },
    { label: "45 dk", minutes: 45, seconds: 0 },
    { label: "1 sa", minutes: 60, seconds: 0 },
];

const pad = (n: number) => String(n).padStart(2, "0");

const formatTime = (totalSecs: number) => {
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    const s = totalSecs % 60;
    if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
    return `${pad(m)}:${pad(s)}`;
};

const LS_KEY = "timer_duration";

interface TimerLS {
    mode: TimerMode;
    remaining: number;
    total: number;
    isRunning: boolean;
    startedAt: number | null;
    isEditing: boolean;
    isFinished: boolean;
    inputHours: number;
    inputMinutes: number;
    inputSeconds: number;
    stopwatchElapsed: number;
    stopwatchIsRunning: boolean;
    stopwatchStartedAt: number | null;
}

const defaultLS: TimerLS = {
    mode: "timer",
    remaining: 25 * 60,
    total: 25 * 60,
    isRunning: false,
    startedAt: null,
    isEditing: true,
    isFinished: false,
    inputHours: 0,
    inputMinutes: 25,
    inputSeconds: 0,
    stopwatchElapsed: 0,
    stopwatchIsRunning: false,
    stopwatchStartedAt: null,
};

function loadLS(): TimerLS {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return defaultLS;
        const saved: TimerLS = { ...defaultLS, ...JSON.parse(raw) };

        if (saved.isRunning && saved.startedAt) {
            const elapsed = Math.floor((Date.now() - saved.startedAt) / 1000);
            const newRemaining = Math.max(0, saved.remaining - elapsed);
            if (newRemaining <= 0) {
                return { ...saved, remaining: 0, isRunning: false, isFinished: true, startedAt: null };
            }
            saved.remaining = newRemaining;
            saved.startedAt = Date.now();
        }

        if (saved.stopwatchIsRunning && saved.stopwatchStartedAt) {
            const elapsed = Math.floor((Date.now() - saved.stopwatchStartedAt) / 1000);
            saved.stopwatchElapsed += elapsed;
            saved.stopwatchStartedAt = Date.now();
        }

        return saved;
    } catch {
        return defaultLS;
    }
}

function saveLS(state: TimerLS) {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch { }
}

const TimerPanel: React.FC<TimerPanelProps> = ({ isOpen, onClose, isHomePage = false }) => {
    const { currentTheme } = useTheme();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const panelPositionClass = isMobile ? "inset-0 top-[60px]" : "top-[60px] right-0 bottom-0 w-80";

    // Load from localStorage on mount
    const initial = loadLS();
    const [activeMode, setActiveMode] = useState<TimerMode>(initial.mode);
    const [inputHours, setInputHours] = useState(initial.inputHours);
    const [inputMinutes, setInputMinutes] = useState(initial.inputMinutes);
    const [inputSeconds, setInputSeconds] = useState(initial.inputSeconds);
    const [totalSeconds, setTotalSeconds] = useState(initial.total);
    const [remaining, setRemaining] = useState(initial.remaining);
    const [isRunning, setIsRunning] = useState(initial.isRunning);
    const [isFinished, setIsFinished] = useState(initial.isFinished);
    const [isEditing, setIsEditing] = useState(initial.isEditing);
    const [stopwatchElapsed, setStopwatchElapsed] = useState(initial.stopwatchElapsed);
    const [stopwatchIsRunning, setStopwatchIsRunning] = useState(initial.stopwatchIsRunning);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const stopwatchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    // Close when another panel requests all panels to close
    useEffect(() => {
        const handleClose = () => {
            if (onClose) onClose();
        };
        window.addEventListener("closeRightPanels", handleClose);
        return () => window.removeEventListener("closeRightPanels", handleClose);
    }, [onClose]);

    const broadcastState = useCallback((overrides: Partial<{
        activeMode: TimerMode;
        remaining: number;
        totalSeconds: number;
        isRunning: boolean;
        stopwatchElapsed: number;
        stopwatchIsRunning: boolean;
    }> = {}) => {
        const snapshot = {
            activeMode,
            remaining,
            totalSeconds,
            isRunning,
            stopwatchElapsed,
            stopwatchIsRunning,
            ...overrides,
        };

        const mode = snapshot.isRunning
            ? "timer"
            : snapshot.stopwatchIsRunning
                ? "stopwatch"
                : snapshot.activeMode;

        window.dispatchEvent(new CustomEvent("timerStateUpdate", {
            detail: {
                mode,
                remaining: snapshot.remaining,
                total: snapshot.totalSeconds,
                elapsed: snapshot.stopwatchElapsed,
                isRunning: snapshot.isRunning || snapshot.stopwatchIsRunning,
            },
        }));
    }, [activeMode, remaining, totalSeconds, isRunning, stopwatchElapsed, stopwatchIsRunning]);

    const playFinishSound = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const beep = (t: number, f: number, d: number) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g); g.connect(ctx.destination);
                o.type = "sine"; o.frequency.value = f;
                g.gain.setValueAtTime(0.3, t);
                g.gain.exponentialRampToValueAtTime(0.001, t + d);
                o.start(t); o.stop(t + d);
            };
            const n = ctx.currentTime;
            beep(n, 880, 0.15); beep(n + 0.2, 1100, 0.15); beep(n + 0.4, 880, 0.15); beep(n + 0.6, 1320, 0.4);
        } catch { }
    }, []);

    // Persist state to localStorage whenever key values change
    useEffect(() => {
        const lsState: TimerLS = {
            mode: activeMode,
            remaining,
            total: totalSeconds,
            isRunning,
            startedAt: isRunning ? Date.now() : null,
            isEditing,
            isFinished,
            inputHours,
            inputMinutes,
            inputSeconds,
            stopwatchElapsed,
            stopwatchIsRunning,
            stopwatchStartedAt: stopwatchIsRunning ? Date.now() : null,
        };
        saveLS(lsState);
        broadcastState();
    }, [activeMode, remaining, totalSeconds, isRunning, isFinished, isEditing, inputHours, inputMinutes, inputSeconds, stopwatchElapsed, stopwatchIsRunning, broadcastState]);

    // Tick
    useEffect(() => {
        if (isRunning) {
            intervalRef.current = setInterval(() => {
                setRemaining((prev) => {
                    const next = prev - 1;
                    if (next <= 0) {
                        clearInterval(intervalRef.current!);
                        setIsRunning(false);
                        setIsFinished(true);
                        playFinishSound();
                        return 0;
                    }
                    return next;
                });
            }, 1000);
        } else {
            if (intervalRef.current) clearInterval(intervalRef.current);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [isRunning, playFinishSound]);

    useEffect(() => {
        if (stopwatchIsRunning) {
            stopwatchIntervalRef.current = setInterval(() => {
                setStopwatchElapsed((prev) => prev + 1);
            }, 1000);
        } else if (stopwatchIntervalRef.current) {
            clearInterval(stopwatchIntervalRef.current);
        }

        return () => {
            if (stopwatchIntervalRef.current) clearInterval(stopwatchIntervalRef.current);
        };
    }, [stopwatchIsRunning]);

    // Broadcast on mount so header badge picks up persisted running state
    useEffect(() => {
        broadcastState();
    }, [broadcastState]);

    // Live focus session from Time Center
    const [focusSession, setFocusSession] = useState<ActiveFocusSessionSummary | null>(() => readActiveFocusSessionSummary());
    const focusTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [, forceUpdate] = useState(0);

    useEffect(() => {
        const handleFocusChange = (e: any) => {
            setFocusSession(e.detail || null);
        };
        window.addEventListener(ACTIVE_FOCUS_SESSION_EVENT, handleFocusChange);

        const checkStorage = () => {
            setFocusSession(readActiveFocusSessionSummary());
        };
        window.addEventListener("storage", checkStorage);

        return () => {
            window.removeEventListener(ACTIVE_FOCUS_SESSION_EVENT, handleFocusChange);
            window.removeEventListener("storage", checkStorage);
        };
    }, []);

    useEffect(() => {
        if (focusSession && focusSession.status === "running") {
            focusTickRef.current = setInterval(() => forceUpdate((n) => n + 1), 1000);
        } else {
            if (focusTickRef.current) clearInterval(focusTickRef.current);
        }
        return () => { if (focusTickRef.current) clearInterval(focusTickRef.current); };
    }, [focusSession]);

    const liveSnapshot = getLiveFocusSessionSnapshot(focusSession);
    const hasFocusSession = !!liveSnapshot;

    const getTotalFromInput = () => inputHours * 3600 + inputMinutes * 60 + inputSeconds;

    const handleStart = () => {
        setActiveMode("timer");
        setStopwatchIsRunning(false);
        if (isEditing) {
            const t = getTotalFromInput();
            if (t <= 0) return;
            setTotalSeconds(t);
            setRemaining(t);
            setIsEditing(false);
            setIsFinished(false);
            setIsRunning(true);
        } else {
            setIsRunning(true);
            setIsFinished(false);
        }
    };

    const handlePause = () => setIsRunning(false);

    const handleReset = () => {
        setIsRunning(false);
        setIsFinished(false);
        setRemaining(totalSeconds);
    };

    const handleNewTimer = () => {
        setActiveMode("timer");
        setIsRunning(false);
        setIsFinished(false);
        setIsEditing(true);
    };

    const handlePreset = (min: number, sec: number) => {
        setActiveMode("timer");
        setInputHours(0);
        setInputMinutes(min);
        setInputSeconds(sec);
        const t = min * 60 + sec;
        setTotalSeconds(t);
        setRemaining(t);
        setIsEditing(true);
        setIsRunning(false);
        setIsFinished(false);
    };

    const handleStopwatchStart = () => {
        setActiveMode("stopwatch");
        setIsRunning(false);
        setIsFinished(false);
        setStopwatchIsRunning(true);
    };

    const handleStopwatchPause = () => setStopwatchIsRunning(false);

    const handleStopwatchReset = () => {
        setActiveMode("stopwatch");
        setStopwatchIsRunning(false);
        setStopwatchElapsed(0);
    };

    const progress = totalSeconds > 0 ? remaining / totalSeconds : 0;
    const radius = 95;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference * (1 - progress);
    const progressColor = progress > 0.5 ? currentTheme.colors.primary : progress > 0.25 ? "#f59e0b" : "#ef4444";
    const stopwatchCycle = stopwatchElapsed > 0 ? (stopwatchElapsed % 60) / 60 : 0;
    const stopwatchStrokeDashoffset = circumference * (1 - stopwatchCycle);
    const timerSubtitle = isRunning && !isFinished
        ? `${formatTime(remaining)} kaldı`
        : isFinished
            ? "Süre doldu!"
            : !isEditing && remaining < totalSeconds
                ? "Duraklatıldı"
                : "Geri sayımı ayarlayın";
    const stopwatchSubtitle = stopwatchIsRunning
        ? `${formatTime(stopwatchElapsed)} geçti`
        : stopwatchElapsed > 0
            ? "Duraklatıldı"
            : "Kronometre hazır";

    if (!isOpen) return null;

    const ringSize = 200;
    const ringCenter = ringSize / 2;

    return (
        <>
            <style>{`
        @keyframes timer-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        .timer-panel-scroll::-webkit-scrollbar { display: none; }
        .timer-panel-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .num-input { background: transparent; border: none; outline: none; text-align: center; font-variant-numeric: tabular-nums; -moz-appearance: textfield; }
        .num-input::-webkit-outer-spin-button, .num-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>

            {isMobile && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={onClose} />}

            {!isHomePage && (
                <div className={`fixed z-49 bg-white ${panelPositionClass}`} />
            )}

            <div className={`fixed z-50 ${panelPositionClass}`}>
                <div
                    className={`flex flex-col h-full w-full ${isHomePage ? "backdrop-blur-lg" : "bg-white border-l border-gray-100"}`}
                    style={
                        isHomePage
                            ? { background: `linear-gradient(135deg, ${currentTheme.colors.background.base}26, ${currentTheme.colors.background.via}33, ${currentTheme.colors.background.to}26)` }
                            : undefined
                    }
                >
                    {/* Header */}
                    <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <h2 className={`text-sm font-semibold ${isHomePage ? "text-white" : "text-gray-800"}`}>
                                Zaman Merkezi
                            </h2>
                            {onClose && (
                                <button
                                    onClick={onClose}
                                    className={`p-1.5 rounded-lg transition-colors ${isHomePage ? "text-white/60 hover:text-white hover:bg-white/10" : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"}`}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        {/* Tab switcher */}
                        <div className="flex mt-3 gap-1">
                            <button
                                onClick={() => setActiveMode("timer")}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                    activeMode === "timer"
                                        ? isHomePage ? "bg-white/15 text-white" : "bg-gray-900 text-white"
                                        : isHomePage ? "text-white/50 hover:text-white/70" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                Zamanlayıcı
                            </button>
                            <button
                                onClick={() => setActiveMode("stopwatch")}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                    activeMode === "stopwatch"
                                        ? isHomePage ? "bg-white/15 text-white" : "bg-gray-900 text-white"
                                        : isHomePage ? "text-white/50 hover:text-white/70" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                Kronometre
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="timer-panel-scroll flex-1 overflow-y-auto px-5 py-5 flex flex-col items-center gap-4">

                        {/* Active Focus Session from Time Center */}
                        {hasFocusSession && liveSnapshot && (() => {
                            const elapsed = liveSnapshot.liveElapsedSeconds || 0;
                            const remaining = liveSnapshot.liveRemainingSeconds;
                            const goal = liveSnapshot.goalDurationSeconds;
                            const progressPct = goal && goal > 0 ? Math.min(100, (elapsed / goal) * 100) : null;
                            const startTime = liveSnapshot.startedAt ? new Date(liveSnapshot.startedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : null;

                            return (
                                <div className={`w-full rounded-xl p-4 mb-2 ${isHomePage ? "bg-white/8 border border-white/10" : "bg-slate-50 border border-slate-100"}`}>
                                    {/* Type & status */}
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${liveSnapshot.isRunning ? "bg-green-500 animate-pulse" : "bg-slate-400"}`} />
                                            <span className={`text-[13px] font-medium ${isHomePage ? "text-white" : "text-slate-700"}`}>
                                                {liveSnapshot.type === "timer" ? "Zamanlayıcı" : liveSnapshot.type === "stopwatch" ? "Kronometre" : "Pomodoro"}
                                            </span>
                                        </div>
                                        <span className={`text-[11px] px-2 py-0.5 rounded-md ${
                                            liveSnapshot.isRunning
                                                ? isHomePage ? "bg-green-500/20 text-green-300" : "bg-green-50 text-green-600"
                                                : isHomePage ? "bg-white/10 text-white/50" : "bg-slate-100 text-slate-500"
                                        }`}>
                                            {liveSnapshot.isRunning ? "Aktif" : liveSnapshot.status === "paused" ? "Duraklatıldı" : liveSnapshot.status}
                                        </span>
                                    </div>

                                    {/* Main time display */}
                                    <div className={`text-2xl font-semibold tabular-nums tracking-tight text-center mb-3 ${isHomePage ? "text-white" : "text-slate-900"}`}>
                                        {liveSnapshot.type === "stopwatch"
                                            ? formatFocusSeconds(elapsed)
                                            : formatFocusSeconds(remaining ?? elapsed)
                                        }
                                    </div>

                                    {/* Progress bar */}
                                    {progressPct !== null && (
                                        <div className={`h-1.5 rounded-full overflow-hidden mb-3 ${isHomePage ? "bg-white/10" : "bg-slate-200"}`}>
                                            <div
                                                className={`h-full rounded-full transition-all duration-1000 ${isHomePage ? "bg-white/60" : "bg-slate-600"}`}
                                                style={{ width: `${progressPct}%` }}
                                            />
                                        </div>
                                    )}

                                    {/* Detail rows */}
                                    <div className={`space-y-1.5 ${isHomePage ? "text-white/50" : "text-slate-500"}`}>
                                        <div className="flex items-center justify-between text-[12px]">
                                            <span>Geçen süre</span>
                                            <span className={`font-medium tabular-nums ${isHomePage ? "text-white/70" : "text-slate-700"}`}>{formatFocusSeconds(elapsed)}</span>
                                        </div>
                                        {remaining !== null && remaining !== undefined && (
                                            <div className="flex items-center justify-between text-[12px]">
                                                <span>Kalan süre</span>
                                                <span className={`font-medium tabular-nums ${isHomePage ? "text-white/70" : "text-slate-700"}`}>{formatFocusSeconds(remaining)}</span>
                                            </div>
                                        )}
                                        {goal && goal > 0 && (
                                            <div className="flex items-center justify-between text-[12px]">
                                                <span>Hedef</span>
                                                <span className={`font-medium tabular-nums ${isHomePage ? "text-white/70" : "text-slate-700"}`}>{formatFocusSeconds(goal)}</span>
                                            </div>
                                        )}
                                        {startTime && (
                                            <div className="flex items-center justify-between text-[12px]">
                                                <span>Başlangıç</span>
                                                <span className={`font-medium ${isHomePage ? "text-white/70" : "text-slate-700"}`}>{startTime}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {activeMode === "timer" ? (
                        <>
                            {/* Ring */}
                            <div className="relative flex items-center justify-center">
                                <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)" }}>
                                    <circle
                                        cx={ringCenter} cy={ringCenter} r={radius} fill="none"
                                        stroke={isHomePage ? "rgba(255,255,255,0.06)" : "#f1f5f9"}
                                        strokeWidth="6"
                                    />
                                    <circle
                                        cx={ringCenter} cy={ringCenter} r={radius} fill="none"
                                        stroke={isFinished ? "#ef4444" : currentTheme.colors.primary}
                                        strokeWidth="6" strokeLinecap="round"
                                        strokeDasharray={circumference}
                                        strokeDashoffset={strokeDashoffset}
                                        style={{ transition: "stroke-dashoffset 1s linear, stroke 0.5s ease" }}
                                    />
                                </svg>

                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    {isEditing ? (
                                        <div className="flex items-center gap-0.5">
                                            <div className="flex flex-col items-center">
                                                <input type="number" min={0} max={23} value={inputHours}
                                                    onChange={(e) => setInputHours(Math.max(0, Math.min(23, Number(e.target.value))))}
                                                    className="num-input w-10 text-2xl font-semibold"
                                                    style={{ color: isHomePage ? "#fff" : "#1e293b" }} />
                                                <span className={`text-[9px] ${isHomePage ? "text-white/40" : "text-gray-400"}`}>sa</span>
                                            </div>
                                            <span className="text-2xl font-light pb-3" style={{ color: isHomePage ? "#fff" : "#94a3b8" }}>:</span>
                                            <div className="flex flex-col items-center">
                                                <input type="number" min={0} max={59} value={inputMinutes}
                                                    onChange={(e) => setInputMinutes(Math.max(0, Math.min(59, Number(e.target.value))))}
                                                    className="num-input w-10 text-2xl font-semibold"
                                                    style={{ color: isHomePage ? "#fff" : "#1e293b" }} />
                                                <span className={`text-[9px] ${isHomePage ? "text-white/40" : "text-gray-400"}`}>dk</span>
                                            </div>
                                            <span className="text-2xl font-light pb-3" style={{ color: isHomePage ? "#fff" : "#94a3b8" }}>:</span>
                                            <div className="flex flex-col items-center">
                                                <input type="number" min={0} max={59} value={inputSeconds}
                                                    onChange={(e) => setInputSeconds(Math.max(0, Math.min(59, Number(e.target.value))))}
                                                    className="num-input w-10 text-2xl font-semibold"
                                                    style={{ color: isHomePage ? "#fff" : "#1e293b" }} />
                                                <span className={`text-[9px] ${isHomePage ? "text-white/40" : "text-gray-400"}`}>sn</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center">
                                            <span
                                                className="text-3xl font-semibold font-mono tracking-wide"
                                                style={{
                                                    color: isFinished ? "#ef4444" : isHomePage ? "#fff" : "#1e293b",
                                                    animation: isFinished ? "timer-pulse 1s ease-in-out infinite" : "none",
                                                }}
                                            >
                                                {formatTime(remaining)}
                                            </span>
                                            <span className={`text-[11px] mt-1 ${
                                                isFinished ? "text-red-400 animate-pulse" :
                                                isRunning ? "animate-pulse" :
                                                isHomePage ? "text-white/40" : "text-gray-400"
                                            }`} style={isRunning && !isFinished ? { color: currentTheme.colors.primary } : undefined}>
                                                {isFinished ? "Süre doldu" : isRunning ? "Çalışıyor" : remaining < totalSeconds ? "Duraklatıldı" : ""}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Presets */}
                            {isEditing && (
                                <div className="flex flex-wrap justify-center gap-1.5 w-full">
                                    {PRESETS.map((p) => (
                                        <button
                                            key={p.label}
                                            onClick={() => handlePreset(p.minutes, p.seconds)}
                                            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                                isHomePage
                                                    ? "bg-white/8 text-white/70 hover:bg-white/15 hover:text-white border border-white/10"
                                                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800 border border-gray-100"
                                            }`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Controls */}
                            <div className="flex items-center gap-2 w-full justify-center mt-1">
                                {!isEditing && (
                                    <button
                                        onClick={handleReset}
                                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                                            isHomePage ? "text-white/50 hover:text-white hover:bg-white/10" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                        }`}
                                        title="Sıfırla"
                                    >
                                        <RotateCcw size={15} />
                                    </button>
                                )}

                                {isFinished ? (
                                    <button
                                        onClick={handleNewTimer}
                                        className="h-10 px-5 rounded-lg flex items-center gap-2 font-medium text-xs transition-colors text-white"
                                        style={{ background: currentTheme.colors.primary }}
                                    >
                                        <RotateCcw size={14} /> Yeni Zamanlayıcı
                                    </button>
                                ) : isEditing ? (
                                    <button
                                        onClick={handleStart}
                                        disabled={getTotalFromInput() <= 0}
                                        className="h-10 px-6 rounded-lg flex items-center gap-2 font-medium text-xs transition-colors text-white disabled:opacity-40 disabled:cursor-not-allowed"
                                        style={{ background: getTotalFromInput() > 0 ? currentTheme.colors.primary : "#9ca3af" }}
                                    >
                                        <Play size={14} fill="white" /> Başlat
                                    </button>
                                ) : isRunning ? (
                                    <button
                                        onClick={handlePause}
                                        className="h-10 px-6 rounded-lg flex items-center gap-2 font-medium text-xs transition-colors bg-amber-500 text-white"
                                    >
                                        <Pause size={14} fill="white" /> Duraklat
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleStart}
                                        className="h-10 px-6 rounded-lg flex items-center gap-2 font-medium text-xs transition-colors text-white"
                                        style={{ background: currentTheme.colors.primary }}
                                    >
                                        <Play size={14} fill="white" /> Devam
                                    </button>
                                )}

                                {!isEditing && !isFinished && (
                                    <button
                                        onClick={handleNewTimer}
                                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                                            isHomePage ? "text-white/50 hover:text-white hover:bg-white/10" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                        }`}
                                        title="Yeni Süre"
                                    >
                                        <AlarmClock size={15} />
                                    </button>
                                )}
                            </div>

                            {/* Info */}
                            {!isEditing && !isFinished && (
                                <p className={`text-[11px] text-center ${isHomePage ? "text-white/30" : "text-gray-400"}`}>
                                    Panel kapatılsa bile sayım devam eder
                                </p>
                            )}

                            {isFinished && (
                                <div className={`w-full rounded-lg p-3 text-center ${isHomePage ? "bg-red-500/10 border border-red-400/20" : "bg-red-50 border border-red-100"}`}>
                                    <Check size={20} className="mx-auto mb-1 text-red-400" />
                                    <p className="text-xs font-medium text-red-500">Süre tamamlandı</p>
                                </div>
                            )}
                        </>
                        ) : (
                        <>
                            {/* Stopwatch Ring */}
                            <div className="relative flex items-center justify-center">
                                <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)" }}>
                                    <circle
                                        cx={ringCenter} cy={ringCenter} r={radius} fill="none"
                                        stroke={isHomePage ? "rgba(255,255,255,0.06)" : "#f1f5f9"}
                                        strokeWidth="6"
                                    />
                                    <circle
                                        cx={ringCenter} cy={ringCenter} r={radius} fill="none"
                                        stroke={currentTheme.colors.primary}
                                        strokeWidth="6" strokeLinecap="round"
                                        strokeDasharray={circumference}
                                        strokeDashoffset={stopwatchStrokeDashoffset}
                                        style={{ transition: "stroke-dashoffset 1s linear", opacity: stopwatchElapsed > 0 || stopwatchIsRunning ? 1 : 0.15 }}
                                    />
                                </svg>

                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-3xl font-semibold font-mono tracking-wide" style={{ color: isHomePage ? "#fff" : "#1e293b" }}>
                                        {formatTime(stopwatchElapsed)}
                                    </span>
                                    <span className={`text-[11px] mt-1 ${
                                        stopwatchIsRunning ? "animate-pulse" : isHomePage ? "text-white/40" : "text-gray-400"
                                    }`} style={stopwatchIsRunning ? { color: currentTheme.colors.primary } : undefined}>
                                        {stopwatchIsRunning ? "Çalışıyor" : stopwatchElapsed > 0 ? "Duraklatıldı" : "Hazır"}
                                    </span>
                                </div>
                            </div>

                            {/* Controls */}
                            <div className="flex items-center gap-2 w-full justify-center mt-1">
                                <button
                                    onClick={handleStopwatchReset}
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                                        isHomePage ? "text-white/50 hover:text-white hover:bg-white/10" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                    }`}
                                    title="Sıfırla"
                                >
                                    <RotateCcw size={15} />
                                </button>

                                {stopwatchIsRunning ? (
                                    <button
                                        onClick={handleStopwatchPause}
                                        className="h-10 px-6 rounded-lg flex items-center gap-2 font-medium text-xs transition-colors bg-amber-500 text-white"
                                    >
                                        <Pause size={14} fill="white" /> Duraklat
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleStopwatchStart}
                                        className="h-10 px-6 rounded-lg flex items-center gap-2 font-medium text-xs transition-colors text-white"
                                        style={{ background: currentTheme.colors.primary }}
                                    >
                                        <Play size={14} fill="white" /> {stopwatchElapsed > 0 ? "Devam" : "Başlat"}
                                    </button>
                                )}
                            </div>

                            <p className={`text-[11px] text-center ${isHomePage ? "text-white/30" : "text-gray-400"}`}>
                                Panel kapatılsa bile kronometre çalışmaya devam eder
                            </p>
                        </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default TimerPanel;
